import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { WifiOff, Wifi, Plane, MapPin, FileText, Phone, Trash2, RefreshCw, CloudDownload, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

/* /offline — single page listing every trip the traveller has pre-cached for
   offline access. Shows connectivity state, lets them refresh, open or forget
   each trip, and surfaces the master emergency contacts page which is also
   cached. Built for the "I'm in the air / in a country with no wifi" moment. */
export default function OfflineHub() {
  const [online, setOnline] = useState(navigator.onLine);
  const [trips, setTrips] = useState([]);

  const refresh = () => {
    try {
      const idx = JSON.parse(localStorage.getItem("tp_offline_trips") || "{}");
      const list = Object.entries(idx).map(([id, m]) => ({ id, ...m }));
      list.sort((a, b) => (b.saved_at || "").localeCompare(a.saved_at || ""));
      setTrips(list);
    } catch { setTrips([]); }
  };
  useEffect(() => {
    refresh();
    const up = () => setOnline(true), down = () => setOnline(false);
    window.addEventListener("online", up); window.addEventListener("offline", down);
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", down); };
  }, []);

  const forget = (id) => {
    try {
      const idx = JSON.parse(localStorage.getItem("tp_offline_trips") || "{}");
      delete idx[id];
      localStorage.setItem("tp_offline_trips", JSON.stringify(idx));
      localStorage.removeItem(`tp_offline_kit_${id}`);
    } catch { /* ignore */ }
    toast.success("Removed from offline cache");
    refresh();
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="offline-hub">
      {/* Hero */}
      <section className="bg-gradient-to-br from-indigo-900 via-sky-800 to-emerald-800 text-white">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 py-10">
          <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.28em] text-sky-200 mb-2">
            {online ? <><Wifi className="w-3.5 h-3.5" /> Online now</> : <><WifiOff className="w-3.5 h-3.5" /> Offline mode</>}
          </div>
          <h1 className="font-serif font-800 text-3xl sm:text-4xl leading-tight">Everything you need, in your pocket</h1>
          <p className="text-sky-100 mt-3 max-w-2xl text-sm sm:text-base">Save a trip for offline and your flight details, documents, airport pages, destination guides, airline contacts, embassy numbers and visa summary all open with no wifi — perfect for the air or a country without data.</p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to="/trips" data-testid="offline-hub-cta-trips"
              className="inline-flex items-center gap-1.5 bg-white text-indigo-900 rounded-full px-4 py-2 font-700 text-sm hover:bg-sky-100">
              <CloudDownload className="w-4 h-4" /> Save a trip for offline
            </Link>
            <Link to="/vault/emergency" data-testid="offline-hub-cta-emergency"
              className="inline-flex items-center gap-1.5 border border-white/30 text-white rounded-full px-4 py-2 font-700 text-sm hover:bg-white/10">
              <ShieldCheck className="w-4 h-4" /> Open emergency card
            </Link>
          </div>
        </div>
      </section>

      {/* Trips cached */}
      <section className="max-w-5xl mx-auto px-5 lg:px-8 py-10">
        <h2 className="font-serif font-800 text-xl sm:text-2xl text-slate-900 mb-4">Cached trips</h2>
        {trips.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-slate-300 p-8 text-center text-sm text-slate-600" data-testid="offline-hub-empty">
            No trips saved for offline yet. Open any trip and tap <b>Save for offline</b> to cache its flights, documents, destinations and contacts.
          </div>
        ) : (
          <div className="grid gap-3">
            {trips.map((t) => (
              <TripCard key={t.id} trip={t} online={online} onForget={() => forget(t.id)} />
            ))}
          </div>
        )}

        {/* What's included */}
        <div className="mt-10 grid sm:grid-cols-2 gap-3">
          <Explainer icon={Plane} tint="text-sky-700 bg-sky-100" title="Flight & transit details" body="Carrier, flight number, times, terminal, gate (when known), confirmation note & layover info — all mid-flight ready." />
          <Explainer icon={FileText} tint="text-emerald-700 bg-emerald-100" title="Documents & tickets" body="Attachments, boarding-pass images, visa pages, insurance cards — stored in your encrypted vault and surfaced here when you're offline." />
          <Explainer icon={MapPin} tint="text-fuchsia-700 bg-fuchsia-100" title="Destination guides" body="Country, city and airport pages for every stop on your trip — read in the air so you're ready to go when you land." />
          <Explainer icon={Phone} tint="text-amber-700 bg-amber-100" title="Emergency & airline contacts" body="Airline customer-service numbers, local emergency services, nearest embassies and your private emergency card." />
        </div>

        <p className="mt-8 text-[11px] text-slate-500 text-center">
          Sensitive items (passport numbers, health vault, home address, payment info) are never put in the offline cache. Open your vault with a PIN when you need them.
        </p>
        <div className="mt-4 text-center">
          <Link to="/trips" className="inline-flex items-center gap-1 text-emerald-700 font-700 text-sm hover:underline" data-testid="offline-hub-trips-link">
            Browse all my trips <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}

function TripCard({ trip, online, onForget }) {
  const saved = trip.saved_at ? new Date(trip.saved_at).toLocaleString() : "—";
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 flex flex-wrap items-start gap-3" data-testid={`offline-trip-${trip.id}`}>
      <div className="w-10 h-10 rounded-xl bg-sky-100 text-sky-700 grid place-items-center shrink-0">
        <Plane className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-serif font-800 text-base text-slate-900 truncate">{trip.title || "Untitled trip"}</div>
        <div className="text-xs text-slate-600 mt-0.5 truncate">
          {trip.stops?.length ? trip.stops.join(" → ") : "No stops listed"}
          {trip.start_date ? ` · ${trip.start_date}` : ""}
        </div>
        <div className="text-[11px] text-slate-500 mt-1">Last synced: {saved}</div>
      </div>
      <div className="flex items-center gap-2 w-full sm:w-auto">
        <Link to={`/trips/${trip.id}`} data-testid={`offline-trip-${trip.id}-open`}
          className="inline-flex items-center gap-1 bg-sky-600 text-white rounded-full px-3 py-1.5 text-xs font-700 hover:bg-sky-500">
          Open
        </Link>
        {online && (
          <Link to={`/trips/${trip.id}`} data-testid={`offline-trip-${trip.id}-resync`}
            className="inline-flex items-center gap-1 border border-slate-300 text-slate-700 rounded-full px-3 py-1.5 text-xs font-700 hover:border-sky-400"
            title="Open the trip and tap Re-sync to refresh">
            <RefreshCw className="w-3 h-3" /> Re-sync
          </Link>
        )}
        <button onClick={onForget} aria-label="Remove from offline" data-testid={`offline-trip-${trip.id}-forget`}
          className="inline-flex items-center gap-1 border border-rose-200 text-rose-700 rounded-full px-3 py-1.5 text-xs font-700 hover:bg-rose-50">
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

function Explainer({ icon: Icon, tint, title, body }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className={`w-9 h-9 rounded-xl grid place-items-center ${tint}`}><Icon className="w-5 h-5" /></div>
      <div className="font-serif font-800 text-base text-slate-900 mt-2">{title}</div>
      <p className="text-xs text-slate-600 mt-1">{body}</p>
    </div>
  );
}
